// ---------------------------------------------------------------------------
// ตรรกะการขายกลาง (POS + ตะกร้า/Checkout ฝั่งลูกค้า) — ให้ผลเหมือนกันทั้งสองทาง
// ---------------------------------------------------------------------------
//   - ราคาคิดใหม่จาก productRepo ทุกครั้ง ห้ามเชื่อราคาที่ client ถือ (ตะกร้าเก็บแค่ productId + qty)
//   - จ่ายสำเร็จแล้ว: บันทึกออเดอร์/บิล -> ใบแจ้งหนี้ -> ใบเสร็จ (ผ่าน settleInvoicePayment ที่มีอยู่ ไม่ออกซ้ำ)
//     -> ตัดสต็อก + stockMoves ระบุสาขา
//   - ทุกขั้นกันซ้ำด้วย transactionId: เรียก finalizeSale ซ้ำ (ปุ่มลองใหม่ของโมดัล QR) ได้ผลเดิม ไม่ตัดสต็อก/ออกใบเสร็จซ้ำ
//   - ไม่แตะ flow การจ่ายเงิน (PaymentBroker) — ไฟล์นี้ทำงานหลังจ่ายสำเร็จเท่านั้น
// ---------------------------------------------------------------------------
import {
  productRepo, inventoryRepo, stockMoveRepo, invoiceRepo, salesOrderRepo, posSaleRepo,
} from "../mock/repositories";
import { gsEnabled } from "./gsClient";
import { nextDocNo } from "./docNumber";
import { settleInvoicePayment } from "./settlement";

export const CART_MAX_QTY = 99;

// ----------------------------- สาขา -----------------------------

/** สาขาที่ใช้บันทึกรายการขาย: ที่เลือกอยู่ -> สาขาหลัก (isMain) -> สาขาเดียวที่มี · ไม่งั้นคืน "" (ให้ผู้ใช้เลือกเอง) */
export const resolveBranchId = (branches = [], selectedId = "") => {
  const list = Array.isArray(branches) ? branches : [];
  if (selectedId && list.some((row) => row.id === selectedId)) return selectedId;
  const main = list.find((row) => row.isMain === true || row.isMain === "true" || row.isMain === "TRUE");
  if (main) return main.id;
  return list.length === 1 ? list[0].id : "";
};

// ----------------------------- เลขรหัส (ไม่สุ่ม) -----------------------------

/**
 * รหัสเรียงต่อร้าน ไม่สุ่ม
 *   - โหมด Sheets + ระบุ docType : ขอจาก `seq.next` (LockService กันชน) -> เช่น C-2026-000001
 *   - โหมด localStorage          : นับจากแถวที่มีอยู่ แล้วข้ามเลขที่ชนกัน -> เช่น C0001 / POS-000001
 */
export const nextRowCode = async (repo, orgId, { prefix, field = "code", width = 4, docType }) => {
  if (docType && gsEnabled()) return (await nextDocNo({ orgId, docType })).docNo;
  const rows = (await repo.all(orgId)) || [];
  const used = new Set(rows.map((row) => String(row[field] || "")));
  let seq = rows.length + 1;
  let code = `${prefix}${String(seq).padStart(width, "0")}`;
  while (used.has(code)) {
    seq += 1;
    code = `${prefix}${String(seq).padStart(width, "0")}`;
  }
  return code;
};

// ----------------------------- ตะกร้าลูกค้า (แยกต่อร้าน เก็บแค่ productId + qty) -----------------------------

export const customerCartKey = (orgId) => `levelup:cart:customer:${orgId}`;

const clampQty = (value) => Math.min(CART_MAX_QTY, Math.max(1, Math.floor(Number(value)) || 1));

export const readCustomerCart = (orgId) => {
  if (!orgId) return [];
  try {
    const raw = JSON.parse(localStorage.getItem(customerCartKey(orgId)) || "[]");
    if (!Array.isArray(raw)) return [];
    const merged = new Map();
    raw.forEach((row) => {
      if (!row || !row.productId) return;
      merged.set(row.productId, clampQty((merged.get(row.productId) || 0) + clampQty(row.quantity)));
    });
    return [...merged].map(([productId, quantity]) => ({ productId, quantity }));
  } catch (err) {
    return [];
  }
};

export const writeCustomerCart = (orgId, rows) => {
  if (!orgId) return;
  const clean = (rows || []).map((row) => ({ productId: row.productId, quantity: clampQty(row.quantity) }));
  localStorage.setItem(customerCartKey(orgId), JSON.stringify(clean));
  window.dispatchEvent(new Event("cart-change"));
};

export const addToCustomerCart = (orgId, productId, quantity = 1) => {
  const rows = readCustomerCart(orgId);
  const existing = rows.find((row) => row.productId === productId);
  if (existing) existing.quantity = clampQty(existing.quantity + quantity);
  else rows.push({ productId, quantity: clampQty(quantity) });
  writeCustomerCart(orgId, rows);
};

export const clearCustomerCart = (orgId) => {
  if (orgId) localStorage.removeItem(customerCartKey(orgId));
  window.dispatchEvent(new Event("cart-change"));
};

// ----------------------------- คิดราคา / ตรวจสต็อก -----------------------------

const NON_STOCK_TYPES = ["service", "package", "course", "rental"];
const money = (value) => Math.round(Number(value) * 100) / 100;

/**
 * คิดราคาใหม่จากสินค้าในระบบ — rows = [{ productId, quantity }] (ฟิลด์อื่นเช่น price ถูกเมิน)
 * คืน { lines, total, missing } · สินค้าที่ไม่มี/ปิดขายอยู่ถูกตัดออกและรายงานใน missing
 */
export const priceLines = async (orgId, rows) => {
  const products = (await productRepo.all(orgId)) || [];
  const byId = new Map(products.map((p) => [p.id, p]));
  const lines = [];
  const missing = [];
  (rows || []).forEach((row) => {
    const product = byId.get(row.productId);
    const quantity = clampQty(row.quantity);
    if (!product || product.isActive === false || !(Number(product.price) > 0)) {
      missing.push(row.productId);
      return;
    }
    const price = money(product.price);
    lines.push({
      productId: product.id,
      name: product.name,
      price,
      quantity,
      total: money(price * quantity),
      // เฉพาะสินค้าจับต้องได้ — บริการ/แพ็กเกจ/คอร์ส/เช่า ไม่เช็คไม่ตัดสต็อก
      trackStock: (product.trackStock === true || product.trackStock === "true" || product.trackStock === "TRUE") && !NON_STOCK_TYPES.includes(product.type),
      unit: product.unit || "",
    });
  });
  return { lines, total: money(lines.reduce((sum, line) => sum + line.total, 0)), missing };
};

const stockRow = (inventory, productId, branchId) => inventory.find((row) => row.productId === productId && row.branchId === branchId);

/** สินค้าที่สต็อกของสาขานั้นไม่พอ — [{ productId, name, need, have }] (ว่าง = ขายได้) · ไม่มีแถวสต็อก = 0 */
export const checkStock = async (orgId, branchId, lines) => {
  const tracked = (lines || []).filter((line) => line.trackStock);
  if (!tracked.length) return [];
  const inventory = (await inventoryRepo.all(orgId)) || [];
  return tracked
    .map((line) => ({ productId: line.productId, name: line.name, need: line.quantity, have: Number(stockRow(inventory, line.productId, branchId)?.quantity) || 0 }))
    .filter((row) => row.have < row.need);
};

/** สร้างแถวสต็อก (qty 0) ให้สินค้าจับต้องได้ที่เพิ่งสร้าง ทุกสาขา — ข้ามสาขาที่มีแถวอยู่แล้ว */
export const ensureInventoryRows = async (orgId, product, branches = []) => {
  if (!product?.id || !product.trackStock) return [];
  const inventory = (await inventoryRepo.all(orgId)) || [];
  const created = [];
  for (const branch of branches) {
    if (stockRow(inventory, product.id, branch.id)) continue;
    created.push(
      await inventoryRepo.create(orgId, {
        productId: product.id,
        productName: product.name,
        branchId: branch.id,
        quantity: 0,
        reorderPoint: 0,
        unit: product.unit || "",
        cost: Number(product.cost) || 0,
        lastCountedAt: null,
      })
    );
  }
  return created;
};

/** ตัดสต็อกตามบรรทัดที่ขาย + บันทึก stockMoves (ref = transactionId กันตัดซ้ำ) · คืน warnings */
export const deductStock = async ({ orgId, branchId, lines, ref, reason, allowNegative = false }) => {
  const warnings = [];
  const tracked = (lines || []).filter((line) => line.trackStock);
  if (!tracked.length) return warnings;
  const inventory = (await inventoryRepo.all(orgId)) || [];
  const moves = ref ? (await stockMoveRepo.all(orgId, (row) => row.ref === ref)) || [] : [];

  for (const line of tracked) {
    if (moves.some((row) => row.productId === line.productId)) continue; // ตัดไปแล้ว
    const row = stockRow(inventory, line.productId, branchId);
    if (!row) {
      warnings.push(`${line.name}: ไม่พบแถวสต็อกของสาขานี้ จึงไม่ได้ตัดสต็อก`);
      continue;
    }
    const have = Number(row.quantity) || 0;
    const taken = allowNegative ? line.quantity : Math.min(have, line.quantity);
    if (have < line.quantity) warnings.push(`${line.name}: สต็อกไม่พอ (เหลือ ${have} ขาย ${line.quantity})${allowNegative ? " สต็อกติดลบ" : " ตัดได้เท่าที่มี"}`);
    await inventoryRepo.update(orgId, row.id, { quantity: have - taken });
    await stockMoveRepo.create(orgId, {
      productId: line.productId,
      productName: line.name,
      branchId,
      type: "out",
      quantity: -taken,
      reason,
      ref: ref || "",
    });
  }
  return warnings;
};

// ----------------------------- ปิดการขายหลังจ่ายสำเร็จ -----------------------------

const KINDS = {
  pos: { repo: posSaleRepo, docType: "pos", prefix: "POS-", field: "billNo", width: 6, reason: "ขายหน้าร้าน (POS)", channel: "pos" },
  checkout: { repo: salesOrderRepo, docType: "sales_order", prefix: "SO-", field: "orderNo", width: 6, reason: "ขายออนไลน์ (Checkout)", channel: "website" },
};

/**
 * บันทึกการขายที่จ่ายแล้วให้ครบ — kind = "pos" | "checkout"
 * lines ต้องมาจาก priceLines (ราคาจากระบบ) · transaction = ผลจากโมดัล QR (ต้องมี transactionId)
 * คืน { order, invoice, receipt, warnings, duplicate }
 */
export const finalizeSale = async ({ orgId, organization, kind, lines, branchId, customer = null, transaction, by = "", allowNegative = false }) => {
  const cfg = KINDS[kind];
  if (!cfg) throw new Error(`ไม่รู้จักประเภทการขาย: ${kind}`);
  const tid = String(transaction?.transactionId || "").trim();
  if (!tid) throw new Error("ไม่พบรหัสธุรกรรม จึงบันทึกการขายไม่ได้");
  if (!lines?.length) throw new Error("ไม่มีรายการสินค้าที่จะบันทึก");

  const items = lines.map((line) => ({ productId: line.productId, name: line.name, price: line.price, quantity: line.quantity, total: line.total }));
  const total = money(lines.reduce((sum, line) => sum + line.total, 0));
  const customerName = customer?.name || "ลูกค้าทั่วไป";

  // 1) ออเดอร์/บิล — หาด้วย transactionId ก่อน (เรียกซ้ำไม่สร้างใหม่)
  let order = ((await cfg.repo.all(orgId, (row) => row.transactionId === tid)) || [])[0] || null;
  const duplicate = !!order;
  if (!order) {
    const number = await nextRowCode(cfg.repo, orgId, { prefix: cfg.prefix, field: cfg.field, width: cfg.width, docType: cfg.docType });
    const common = { [cfg.field]: number, branchId, items, total, transactionId: tid, customerId: customer?.id || "", customerName };
    order = await cfg.repo.create(
      orgId,
      kind === "pos"
        ? { ...common, paymentMethod: "qr" }
        : { ...common, channel: cfg.channel, subtotal: total, shippingFee: 0, discount: 0, paymentStatus: "paid", shippingStatus: "pending" }
    );
  }

  // 2) ใบแจ้งหนี้ผูกออเดอร์ -> 3) settle (ใบเสร็จ ออกครั้งเดียวต่อ transactionId)
  let invoice = ((await invoiceRepo.all(orgId, (row) => row.sourceId === order.id)) || [])[0] || null;
  if (!invoice) {
    const { docNo } = await nextDocNo({ orgId, organization, docType: "invoice" });
    invoice = await invoiceRepo.create(orgId, {
      docNo,
      customerId: customer?.id || "",
      customerName,
      customerPhone: customer?.phone || "",
      items,
      subtotal: total,
      discount: 0,
      vatPercent: 0, // ราคาสินค้าเป็นยอดที่ลูกค้าจ่ายจริง ไม่บวก VAT เพิ่มจากยอด QR
      total,
      paidAmount: 0,
      status: "unpaid",
      dueAt: new Date().toISOString(),
      branchId,
      sourceType: kind,
      sourceId: order.id,
      sourceNo: order[cfg.field],
    });
  }
  const settled = await settleInvoicePayment({
    orgId, organization, invoiceId: invoice.id, transactionId: tid, amount: total, method: "qr", refNo: transaction.refNo || "", by,
  });

  // 4) ตัดสต็อก
  const warnings = await deductStock({ orgId, branchId, lines, ref: tid, allowNegative, reason: `${cfg.reason} ${order[cfg.field]}` });

  // 5) ผูกเลขเอกสารกลับที่ออเดอร์
  if (order.receiptNo !== settled.receipt.docNo || order.invoiceNo !== settled.invoice.docNo) {
    order = (await cfg.repo.update(orgId, order.id, { invoiceId: invoice.id, invoiceNo: settled.invoice.docNo, receiptId: settled.receipt.id, receiptNo: settled.receipt.docNo })) || order;
  }

  return { order, invoice: settled.invoice, receipt: settled.receipt, warnings: [...warnings, ...(settled.warning ? [settled.warning] : [])], duplicate };
};
